/**
 * Thin HTTP transport wrapping `fetch` + zod validation.
 *
 * Why hand-rolled rather than axios / ky / openapi-fetch:
 *   - `fetch` is available natively in every environment CORTEX targets
 *     (Browsers, Node 22, Cloudflare Workers, Bun, Deno, Cloud Run).
 *     Bundling a third-party HTTP library is ~30KB of avoidable weight
 *     in the SPA.
 *   - The transport is small enough to audit at a glance, and that
 *     matters: every request the SPA makes goes through this file, so
 *     it's the single place where a future PR adds auth, retries, or
 *     telemetry hooks.
 *
 * What the transport does for you:
 *   - Builds the URL from `baseUrl + path` (relative paths supported).
 *   - Sends `Accept: application/json`.
 *   - Optionally propagates a caller-supplied `X-Request-ID` for
 *     correlation against the API's request log.
 *   - Parses the response body as JSON exactly once.
 *   - On non-2xx, attempts to parse the body as the CORTEX error
 *     envelope; raises `ApiClientError` either way (server code or
 *     `malformed_response`).
 *   - On 2xx, validates the body against the supplied zod schema; raises
 *     `ApiClientError('malformed_response')` if the shape diverges.
 *   - Honours `AbortSignal` for cancellation.
 *
 * What it does NOT do (and why):
 *   - Retries: the policy depends on the endpoint (mutating endpoints
 *     must be idempotency-keyed before retrying). Pushed to the caller.
 *   - Auth: lands in PR #8. The `defaultHeaders` hook is the seam.
 *   - Timeouts: pushed to the caller via `AbortSignal`. Callers usually
 *     compose timeout + user-cancel signals, so a transport-level
 *     timeout would silently override.
 */

import type { z } from 'zod';
import { ApiClientError } from './errors.js';
import { ErrorEnvelopeSchema } from '../schemas/error.js';

export interface TransportOptions {
  readonly baseUrl: string;
  /** Override the global `fetch` (tests inject a stub). */
  readonly fetch?: typeof globalThis.fetch;
  /**
   * Headers attached to every request. May be either a static record
   * (fixed at client-construction time) or a function evaluated at
   * each request — used by the SPA to inject the dev-bypass subject
   * header that the user can change at runtime without re-creating
   * the client.
   */
  readonly defaultHeaders?:
    | Readonly<Record<string, string>>
    | (() => Readonly<Record<string, string>>);
}

export interface RequestOptions {
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
  /** Caller-supplied request id; echoed in `X-Request-ID`. */
  readonly requestId?: string;
}

export interface Transport {
  get<TSchema extends z.ZodTypeAny>(
    path: string,
    schema: TSchema,
    options?: RequestOptions,
  ): Promise<z.infer<TSchema>>;
  post<TSchema extends z.ZodTypeAny>(
    path: string,
    schema: TSchema,
    body: unknown,
    options?: RequestOptions,
  ): Promise<z.infer<TSchema>>;
  patch<TSchema extends z.ZodTypeAny>(
    path: string,
    schema: TSchema,
    body: unknown,
    options?: RequestOptions,
  ): Promise<z.infer<TSchema>>;
  put<TSchema extends z.ZodTypeAny>(
    path: string,
    schema: TSchema,
    body: unknown,
    options?: RequestOptions,
  ): Promise<z.infer<TSchema>>;
  delete<TSchema extends z.ZodTypeAny>(
    path: string,
    schema: TSchema,
    options?: RequestOptions,
  ): Promise<z.infer<TSchema>>;
}

const buildUrl = (baseUrl: string, path: string): string => {
  const normalisedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const normalisedPath = path.startsWith('/') ? path : `/${path}`;
  return `${normalisedBase}${normalisedPath}`;
};

const buildHeaders = (
  defaults: Readonly<Record<string, string>> | undefined,
  request: Readonly<Record<string, string>> | undefined,
  requestId: string | undefined,
): Headers => {
  const headers = new Headers();
  headers.set('Accept', 'application/json');
  if (defaults) {
    for (const [k, v] of Object.entries(defaults)) headers.set(k, v);
  }
  if (request) {
    for (const [k, v] of Object.entries(request)) headers.set(k, v);
  }
  if (requestId !== undefined && !headers.has('X-Request-ID')) {
    headers.set('X-Request-ID', requestId);
  }
  return headers;
};

const parseResponse = async <TSchema extends z.ZodTypeAny>(
  response: Response,
  schema: TSchema,
): Promise<z.infer<TSchema>> => {
  const serverRequestId = response.headers.get('X-Request-ID');

  let body: unknown = null;
  const text = await response.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch (cause) {
      throw new ApiClientError('malformed_response', response.status, serverRequestId, {
        cause,
        body: text,
      });
    }
  }

  if (!response.ok) {
    const envelope = ErrorEnvelopeSchema.safeParse(body);
    if (envelope.success) {
      throw new ApiClientError(
        envelope.data.error.code,
        response.status,
        envelope.data.error.requestId,
        { body },
      );
    }
    throw new ApiClientError('malformed_response', response.status, serverRequestId, {
      body,
    });
  }

  const parsed = schema.safeParse(body);
  if (!parsed.success) {
    throw new ApiClientError('malformed_response', response.status, serverRequestId, {
      cause: parsed.error,
      body,
    });
  }

  return parsed.data;
};

export const createTransport = (options: TransportOptions): Transport => {
  const fetchImpl = options.fetch ?? globalThis.fetch;
  if (typeof fetchImpl !== 'function') {
    throw new Error(
      '@cortex/domain: no `fetch` available. Provide one via TransportOptions.fetch.',
    );
  }

  const resolveDefaultHeaders = (): Readonly<Record<string, string>> | undefined => {
    const defaults = options.defaultHeaders;
    if (typeof defaults === 'function') return defaults();
    return defaults;
  };

  const request = async <TSchema extends z.ZodTypeAny>(
    method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    schema: TSchema,
    requestOptions: RequestOptions | undefined,
    jsonBody?: unknown,
  ): Promise<z.infer<TSchema>> => {
    const url = buildUrl(options.baseUrl, path);
    const headers = buildHeaders(
      resolveDefaultHeaders(),
      requestOptions?.headers,
      requestOptions?.requestId,
    );
    const hasBody = method === 'POST' || method === 'PATCH' || method === 'PUT';
    if (hasBody) {
      headers.set('Content-Type', 'application/json');
    }

    let response: Response;
    try {
      response = await fetchImpl(url, {
        method,
        headers,
        signal: requestOptions?.signal,
        body: hasBody ? JSON.stringify(jsonBody) : undefined,
      });
    } catch (cause) {
      throw new ApiClientError('network_error', 0, null, { cause });
    }

    return parseResponse(response, schema);
  };

  return {
    get: (path, schema, requestOptions) => request('GET', path, schema, requestOptions),
    post: (path, schema, body, requestOptions) =>
      request('POST', path, schema, requestOptions, body),
    patch: (path, schema, body, requestOptions) =>
      request('PATCH', path, schema, requestOptions, body),
    put: (path, schema, body, requestOptions) => request('PUT', path, schema, requestOptions, body),
    delete: (path, schema, requestOptions) => request('DELETE', path, schema, requestOptions),
  };
};
